import React, { useMemo, useState } from "react";

import BookingFilters from "../../components/Admin/BookingFilters";
import BookingsTable from "../../components/Admin/BookingsTable";
import StatPill from "../../components/Admin/StatPill";
import ThemeToggle from "../../components/Admin/ThemeToggle";

import { useBookings } from "../../hooks/useBookings";
import { useVehicles } from "../../hooks/useVehicles";

export default function AllBookingsPage() {
  const {
    bookings,
    loading: bookingsLoading,
    err: bookingsErr,
    updateStatus,
    deleteById: deleteBookingById,
  } = useBookings();

  const { vehicles, loading: vehiclesLoading, err: vehiclesErr } = useVehicles();

  const loading = bookingsLoading || vehiclesLoading;
  const err = bookingsErr || vehiclesErr;

  const [filterStatus, setFilterStatus] = useState("All");
  const [filterVehicle, setFilterVehicle] = useState("All");
  const [dateOrder, setDateOrder] = useState("closest");

  const counts = useMemo(() => {
    const total = bookings.length;
    const pending = bookings.filter((b) => b.status === "Pending").length;
    const confirmed = bookings.filter((b) => b.status === "Confirmed").length;
    const completed = bookings.filter((b) => b.status === "Completed").length;
    const cancelled = bookings.filter((b) => b.status === "Cancelled").length;
    return { total, pending, confirmed, completed, cancelled, vehicles: vehicles.length };
  }, [bookings, vehicles]);

  const filteredSorted = useMemo(() => {
    const now = new Date();
    const byStatus = (b) => (filterStatus === "All" ? true : b.status === filterStatus);
    const byVehicle = (b) => (filterVehicle === "All" ? true : String(b.vehicle_id) === String(filterVehicle));

    return [...bookings]
      .filter(byStatus)
      .filter(byVehicle)
      .sort((a, b) => {
        const da = new Date(a.start_date);
        const db = new Date(b.start_date);
        const diffA = Math.abs(da - now);
        const diffB = Math.abs(db - now);
        return dateOrder === "closest" ? diffA - diffB : diffB - diffA;
      });
  }, [bookings, filterStatus, filterVehicle, dateOrder]);

  const onUpdateStatus = async (id, newStatus) => {
    await updateStatus(id, newStatus);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    await deleteBookingById(id);
  };

  return (
    <div className="p-6 space-y-8 min-h-screen bg-gray-50 text-gray-900 dark:bg-slate-900 dark:text-gray-100">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">All Bookings</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            View and manage every booking. Use filters to narrow down results.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/admin"
            className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-medium bg-blue-600 text-white shadow-sm hover:bg-blue-700"
          >
            Go back to dashboard
          </a>
          <ThemeToggle />
        </div>
      </header>

      {/* Stats strip */}
      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatPill label="Total" value={counts.total} />
        <StatPill label="Pending" value={counts.pending} tone="amber" />
        <StatPill label="Confirmed" value={counts.confirmed} tone="blue" />
        <StatPill label="Completed" value={counts.completed} tone="emerald" />
        <StatPill label="Cancelled" value={counts.cancelled} tone="rose" />
        <StatPill label="Vehicles" value={counts.vehicles} tone="slate" />
      </section>

      {/* Filters + Table */}
      <section className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="flex flex-col gap-3 p-5 border-b border-gray-100 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Bookings</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Showing {filteredSorted.length} result(s) by proximity to today
            </p>
          </div>
          <div className="w-full sm:w-auto">
            <BookingFilters
              vehicles={vehicles}
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              filterVehicle={filterVehicle}
              setFilterVehicle={setFilterVehicle}
              dateOrder={dateOrder}
              setDateOrder={setDateOrder}
            />
          </div>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
          ) : err ? (
            <div className="text-sm text-rose-600">Error: {err}</div>
          ) : (
            <BookingsTable
              bookings={filteredSorted}
              vehicles={vehicles}
              onUpdateStatus={onUpdateStatus}
              onDelete={onDelete}
            />
          )}
        </div>
      </section>
    </div>
  );
}
